import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';

interface MarkdownProps {
  children: string;
  className?: string;
}

const SAFE_HREF = /^(https?:|mailto:)/i;
const SAFE_IMG = /^(https?:|data:image\/(png|jpe?g|webp|gif);)/i;

function safeHref(url?: string) {
  if (!url) return undefined;
  return SAFE_HREF.test(url) ? url : undefined;
}
function safeImgSrc(url?: string) {
  if (!url) return undefined;
  return SAFE_IMG.test(url) ? url : undefined;
}

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), 'target', 'rel'],
    img: [...(defaultSchema.attributes?.img || [])],
    code: [...(defaultSchema.attributes?.code || []), ['className', /^language-/]],
    h1: [...(defaultSchema.attributes?.h1 || []), 'id'],
    h2: [...(defaultSchema.attributes?.h2 || []), 'id'],
    h3: [...(defaultSchema.attributes?.h3 || []), 'id'],
    h4: [...(defaultSchema.attributes?.h4 || []), 'id'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
    src: ['http', 'https', 'data'],
  },
};

export function Markdown({ children, className }: MarkdownProps) {
  return (
    <div className={`prose prose-sm max-w-none prose-headings:font-display prose-a:text-primary break-words ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, [rehypeSanitize, sanitizeSchema]]}
        components={{
          img: ({ src, alt }) => {
            const safe = safeImgSrc(typeof src === 'string' ? src : undefined);
            if (!safe) return null;
            return (
              <img
                src={safe}
                alt={alt || ''}
                loading="lazy"
                className="rounded-lg border max-h-96 my-3 object-contain"
              />
            );
          },
          a: ({ href, children }) => {
            const safe = safeHref(href);
            if (!safe) return <span>{children}</span>;
            return (
              <a href={safe} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

export function stripMarkdown(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#>*_~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
